import React, { Fragment, PureComponent } from 'react';
import { formShape } from 'rc-form';
import styled from 'styled-components';
import TextField from '../../SolvPatternLibrary/TextField';
import SelectField from '../../SolvPatternLibrary/SelectTextField';
import { dobYearValidator, emptyFieldValidator } from '../../../core/util/rcForm/validators';
import { getDays, getMonths } from '../../SolvPatternLibrary/BirthDateInput/util';
import { keypressToMonth, standardizeYear } from '../../../core/util/date';
import { normalizeMonthFast } from '../utils';
import { leftPad } from '../../../core/util/string';
import { BIRTHDAY_ID, DAY_ID, MONTH_ID, YEAR_ID } from './ids';
import { string } from 'prop-types';
import moment from 'moment-timezone';

const MonthInput = styled(SelectField)`
  width: 140px;
`;

const DayInput = styled(SelectField)`
  width: 70px;
`;

const YearInput = styled(TextField)`
  width: 84px;
`;

export default class DateOfBirthInput extends PureComponent {
  static propTypes = {
    form: formShape.isRequired,
    birthDate: string,
  };

  componentDidMount = () => {
    this.updateBirthdayValue();
  };

  get birthdayProps() {
    const { getFieldProps, getFieldsValue } = this.props.form;
    const { [DAY_ID]: dayValue, [MONTH_ID]: monthValue, [YEAR_ID]: yearValue } = getFieldsValue([
      DAY_ID,
      MONTH_ID,
      YEAR_ID,
    ]);

    const newValue = `${yearValue}-${normalizeMonthFast(monthValue)}-${leftPad('0', 2, dayValue)}`;
    return {
      ...getFieldProps(BIRTHDAY_ID),
      id: BIRTHDAY_ID,
      value: newValue,
    };
  }

  get dayProps() {
    const { form, birthDate } = this.props;
    const error = form.getFieldError(DAY_ID);
    return {
      ...form.getFieldProps(DAY_ID, {
        initialValue: (birthDate && moment(birthDate).format('D')) || '',
        validateTrigger: ['onBlur', 'onChange'],
        rules: [
          {
            required: true,
            validator: emptyFieldValidator,
          },
        ],
        onBlur: this.updateBirthdayValue,
      }),
      value: form.getFieldValue(DAY_ID) || '',
      id: DAY_ID,
      label: 'Day',
      error: !!error,
      selectOptions: getDays(),
    };
  }

  get monthProps() {
    const { form, birthDate } = this.props;
    const error = form.getFieldError(MONTH_ID);
    return {
      ...form.getFieldProps(MONTH_ID, {
        initialValue: (birthDate && moment(birthDate).format('MMMM')) || '',
        validateTrigger: ['onBlur', 'onChange'],
        rules: [
          {
            required: true,
            validator: emptyFieldValidator,
          },
        ],
        onBlur: () => this.unregisterKeyListener(this.updateBirthdayValue),
      }),
      value: form.getFieldValue(MONTH_ID) || '',
      id: MONTH_ID,
      label: 'Month',
      error: !!error,
      onFocus: this.registerKeyListener,
      selectOptions: getMonths(),
    };
  }

  get yearProps() {
    const { form, birthDate } = this.props;
    const error = form.getFieldError(YEAR_ID);
    return {
      ...form.getFieldProps(YEAR_ID, {
        initialValue: (birthDate && moment(birthDate).format('YYYY')) || '',
        validateTrigger: 'onBlur',
        rules: [
          {
            required: true,
            validator: dobYearValidator,
          },
        ],
      }),
      value: form.getFieldValue(YEAR_ID) || '',
      id: YEAR_ID,
      label: 'Year',
      error: !!error,
      type: 'number',
      inputProps: { maxLength: 4 },
      onBlur: ({ target: { value } }) =>
        form.setFieldsValue({ [YEAR_ID]: String(standardizeYear(value)) }, () => {
          form.validateFields([YEAR_ID]);
          this.updateBirthdayValue();
        }),
    };
  }

  updateBirthdayValue = () => {
    const { getFieldsValue, setFieldsValue } = this.props.form;
    const { [DAY_ID]: dayValue, [MONTH_ID]: monthValue, [YEAR_ID]: yearValue } = getFieldsValue([
      DAY_ID,
      MONTH_ID,
      YEAR_ID,
    ]);

    const value = `${yearValue}-${normalizeMonthFast(monthValue)}-${leftPad('0', 2, dayValue)}`;

    setFieldsValue({ [BIRTHDAY_ID]: value });
  };

  registerKeyListener = () => {
    if (this.listener) removeEventListener('keydown', this.listener);

    const { setFieldsValue, getFieldsValue } = this.props.form;
    this.listener = keypressToMonth(m => {
      const { [MONTH_ID]: monthValue } = getFieldsValue([MONTH_ID]);

      let updatedMonth = m;
      if (monthValue === 'January') {
        switch (m) {
          case 'January':
            updatedMonth = 'November';
            break;
          case 'February':
            updatedMonth = 'December';
            break;
          default:
            updatedMonth = m;
        }
      }

      setFieldsValue({ [MONTH_ID]: updatedMonth });
      this.updateBirthdayValue();
    }, 'sign-up-form');
    addEventListener('keydown', this.listener);
  };

  unregisterKeyListener = cb => {
    removeEventListener('keydown', this.listener);
    typeof cb === 'function' && cb();
  };

  render = () => (
    <Fragment>
      <MonthInput {...this.monthProps} />
      <DayInput {...this.dayProps} />
      <YearInput {...this.yearProps} />
      <input type="hidden" {...this.birthdayProps} />
    </Fragment>
  );
}
